import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { BellRinging, BellSimpleSlash, CheckCircle, Spinner } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useCustomer } from "@/context/CustomerContext";

export const variantLabel = (v) => v?.option_values?.find((o) => o.type === "size")?.value || v?.title || "";

export default function WatchSizeButton({ productId, variant }) {
  const { customer, setCustomer } = useCustomer();
  const location = useLocation();
  const [busy, setBusy] = useState(false);
  const label = variantLabel(variant);

  if (!customer) {
    return (
      <p data-testid="watch-size-signin" className="mt-3 text-xs text-foreground/70">
        or <Link to={`/account/login?next=${encodeURIComponent(location.pathname)}`} className="text-[#C84A31] underline">sign in</Link> to watch {label} on your wishlist
      </p>
    );
  }

  const watching = (customer.watch?.[productId] || []).includes(label);

  async function watch() {
    setBusy(true);
    try {
      const { data } = await api.post("/account/watch", { product_id: productId, variant_id: variant.id });
      setCustomer((c) => ({ ...c, wishlist: data.ids, watch: data.watch }));
      toast.success(`Watching ${data.label}`, { description: "Saved to your wishlist — we'll email you when it's back." });
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  }
  async function unwatch() {
    setBusy(true);
    try {
      const next = (customer.watch?.[productId] || []).filter((l) => l !== label);
      const { data } = await api.put(`/account/wishlist/${productId}/watch`, { labels: next });
      setCustomer((c) => ({ ...c, watch: data.watch }));
      toast.success(`Stopped watching ${label}`);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  }

  return (
    <div data-testid="watch-size" className="mt-3 flex flex-wrap items-center gap-3">
      {watching ? (
        <>
          <span data-testid="watch-size-on" className="flex items-center gap-1.5 text-sm text-[#2A4B3C]">
            <CheckCircle size={18} weight="fill" /> Watching {label}{customer.restock_alerts ? "" : " (emails off in Settings)"}
          </span>
          <button type="button" data-testid="watch-size-off" onClick={unwatch} disabled={busy} className="text-xs text-muted-foreground hover:text-[#C84A31] inline-flex items-center gap-1">
            <BellSimpleSlash size={14} /> Stop
          </button>
        </>
      ) : (
        <Button type="button" data-testid="watch-size-btn" onClick={watch} disabled={busy} className="btn-primary h-10 rounded-sm px-4">
          {busy ? <Spinner size={16} className="mr-2 animate-spin" /> : <BellRinging size={16} className="mr-2" />}
          Watch {label} on my wishlist
        </Button>
      )}
    </div>
  );
}
